// src/pages/admin/AdminCategories.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import './admin-books.css';
import '../../components/books/books-shared.css';

import {
  engine,
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';

import {
  Spinner, EmptyState, Modal, ConfirmModal,
} from '../../components/books/BooksSharedUI.jsx';

const MIGRATION_FLAG = 'project-categories-migrated-v1';

export default function AdminCategoriesPage() {
  const { user } = useAuth();
  if (!user || user.role !== 'admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🔒</div>
        <h2>دسترسی محدود</h2>
      </div>
    );
  }
  return (
    <BooksEngineProvider user={user}>
      <Inner />
    </BooksEngineProvider>
  );
}

function Inner() {
  const navigate = useNavigate();
  const { toast } = useBooksEngine();
  const { ready } = useEngineReady();

  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const reload = useCallback(() => {
    try {
      setCategories(dataEngine.category.list());
      setProjects(dataEngine.project.list({ type: 'freeStudy', includeArchived: true }));
    } catch (e) {
      console.warn('[AdminCategories] reload failed:', e);
    }
  }, []);

  // 🆕 Migration: اولین بار، دسته‌بندی‌های آزاد پروژه‌های موجود را تبدیل کن
  useEffect(() => {
    if (!ready) return;
    if (engine.get(MIGRATION_FLAG, null)) return;
    if (migrating) return;

    try {
      const existingCats = dataEngine.category.list();
      const allProjects = dataEngine.project.list({ type: 'freeStudy', includeArchived: true });

      // اگر هیچ پروژه‌ای هم نداریم، flag بگذار و برو
      if (allProjects.length === 0) {
        engine.set(MIGRATION_FLAG, { at: Date.now(), migrated: 0 });
        return;
      }

      setMigrating(true);

      const existingByName = new Map(existingCats.map((c) => [c.name, c]));
      const toCreate = new Map(); // name → [projects]

      for (const p of allProjects) {
        // اگر categoryId دارد و در collection هست → skip
        if (p.categoryId && dataEngine.category.get(p.categoryId)) continue;

        const name = String(p.category || '').trim();
        if (!name) continue;

        if (existingByName.has(name)) {
          const cat = existingByName.get(name);
          try {
            dataEngine.project.update(p.id, { categoryId: cat.id });
          } catch {}
        } else {
          if (!toCreate.has(name)) toCreate.set(name, []);
          toCreate.get(name).push(p);
        }
      }

      let sortIdx = existingCats.length;
      let migratedCount = 0;
      for (const [name, projs] of toCreate) {
        const res = dataEngine.category.create({ name, sortIndex: sortIdx++ });
        if (res.ok) {
          migratedCount++;
          for (const p of projs) {
            try {
              dataEngine.project.update(p.id, { categoryId: res.data.id });
            } catch {}
          }
        }
      }

      engine.set(MIGRATION_FLAG, {
        at: Date.now(),
        migrated: migratedCount,
        totalScanned: allProjects.length,
      });

      if (migratedCount > 0) {
        toast?.(`${migratedCount} دسته‌بندی از پروژه‌های موجود استخراج شد`, 'success');
      }
    } catch (e) {
      console.warn('[AdminCategories] migration failed:', e);
    } finally {
      setMigrating(false);
    }
  }, [ready, migrating, toast]);

  // بارگذاری اولیه + روی هر تغییر
  useEffect(() => {
    if (!ready) return;
    reload();
  }, [ready, refreshTick, reload]);

  // گوش دادن به تغییرات registry
  useEffect(() => {
    const bump = () => setRefreshTick((t) => t + 1);
    const u1 = engine.on('registry:category:changed', bump);
    const u2 = engine.on('registry:project:changed', bump);
    return () => { try { u1(); } catch {} try { u2(); } catch {} };
  }, []);

  // محاسبه تعداد پروژه‌های هر دسته
  const projectCountByCategory = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const cid = p.categoryId;
      if (cid) {
        map.set(cid, (map.get(cid) || 0) + 1);
      }
    }
    return map;
  }, [projects]);

  // پروژه‌های بدون categoryId (legacy یا بدون دسته)
  const orphanCount = useMemo(
    () => projects.filter((p) => !p.categoryId && (p.category || '').trim()).length,
    [projects]
  );

  // ─── عملیات ───

  const handleAdd = () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast?.('نام دسته‌بندی الزامی است', 'error');
      return;
    }
    const res = dataEngine.category.create({ name });
    if (res.ok) {
      toast?.('دسته‌بندی اضافه شد', 'success');
      setNewCategoryName('');
      setAddingCategory(false);
      setRefreshTick((t) => t + 1);
    } else {
      const err = (res.errors || [])[0];
      const msg = {
        duplicate_name: 'دسته‌بندی با این نام قبلاً وجود دارد',
        name_required: 'نام الزامی است',
        name_too_long: 'نام بیش از ۱۰۰ کاراکتر است',
      }[err] || err;
      toast?.(msg, 'error');
    }
  };

  const handleStartRename = (cat) => {
    setEditingCategory(cat);
    setEditingName(cat.name);
  };

  const handleRename = () => {
    if (!editingCategory) return;
    const name = editingName.trim();
    if (!name) {
      toast?.('نام الزامی است', 'error');
      return;
    }
    if (name === editingCategory.name) {
      setEditingCategory(null);
      return;
    }

    const catId = editingCategory.id;
    const oldName = editingCategory.name;

    const res = dataEngine.category.update(catId, { name });
    if (!res.ok) {
      const err = (res.errors || [])[0];
      toast?.(err === 'duplicate_name' ? 'دسته‌بندی با این نام قبلاً وجود دارد' : err, 'error');
      return;
    }

    // 🆕 آپدیت گروهی همه‌ی پروژه‌ها در یک عملیات اتمیک
    const batchRes = dataEngine.project.batchUpdate((p) => {
      const matches =
        p.categoryId === catId ||
        (!p.categoryId && (p.category || '') === oldName);
      if (!matches) return null;
      return { categoryId: catId, category: name };
    });

    const updatedCount = batchRes?.count || 0;

    toast?.(
      updatedCount > 0
        ? `نام دسته‌بندی و ${updatedCount} پروژه به‌روزرسانی شد`
        : 'نام دسته‌بندی به‌روزرسانی شد',
      'success'
    );
    setEditingCategory(null);
    setRefreshTick((t) => t + 1);
  };

  const handleDelete = (cat) => {
    // 🆕 خواندن تازه از registry (نه state کهنه)
    let allProjectsFresh = [];
    try {
      allProjectsFresh = dataEngine.project.list({
        type: 'freeStudy',
        includeArchived: true,
      });
    } catch {}
    const affected = allProjectsFresh.filter((p) => p.categoryId === cat.id);
    setConfirmDelete({ category: cat, affectedCount: affected.length });
  };

  const confirmDeleteCategory = () => {
    if (!confirmDelete) return;
    const { category, affectedCount } = confirmDelete;

    if (affectedCount > 0) {
      // 🆕 پاک کردن categoryId از پروژه‌ها در یک عملیات اتمیک
      dataEngine.project.batchUpdate((p) => {
        if (p.categoryId !== category.id) return null;
        return { categoryId: null, category: '' };
      });
    }

    const res = dataEngine.category.remove(category.id);
    if (res.ok) {
      toast?.(
        affectedCount > 0
          ? `دسته‌بندی حذف شد و ${affectedCount} پروژه بدون دسته‌بندی شدند`
          : 'دسته‌بندی حذف شد',
        'success'
      );
      setConfirmDelete(null);
      setRefreshTick((t) => t + 1);
    } else {
      toast?.('خطا در حذف دسته‌بندی', 'error');
    }
  };

  const handleMove = (cat, direction) => {
    const list = [...categories];
    const idx = list.findIndex((c) => c.id === cat.id);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    const orderedIds = list.map((c) => c.id);
    dataEngine.category.reorder(orderedIds);
    setRefreshTick((t) => t + 1);
  };

  const handleRunMigration = () => {
    // اجبار به اجرای مجدد migration
    try {
      engine.remove(MIGRATION_FLAG);
      setRefreshTick((t) => t + 1);
    } catch {}
  };

  if (!ready) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="ab-page" style={{ padding: '20px', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <button
          className="ab-btn ab-btn-ghost"
          onClick={() => navigate('/admin/books')}
          style={{ marginBottom: 16 }}
        >
          ← بازگشت به پنل مدیریت
        </button>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem' }}>
              📂 مدیریت دسته‌بندی‌ها
            </h1>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              دسته‌بندی‌هایی که معلمان از بین‌شان برای دوره‌های مطالعه آزاد انتخاب می‌کنند
            </p>
          </div>

          <button
            className="ab-btn ab-btn-primary"
            onClick={() => setAddingCategory(true)}
          >
            ➕ افزودن دسته‌بندی
          </button>
        </div>
      </div>

      {orphanCount > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: 12,
          color: '#92400e',
          fontSize: '0.85rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <span>
            ⚠️ {orphanCount} پروژه دارای دسته‌بندی قدیمی (متنی) است که با دسته‌بندی‌های سیستم همگام نشده.
          </span>
          <button
            className="ab-btn ab-btn-ghost"
            onClick={handleRunMigration}
            style={{ fontSize: '0.8rem' }}
          >
            🔄 همگام‌سازی
          </button>
        </div>
      )}

      {categories.length === 0 ? (
        <div style={{ padding: 40 }}>
          <EmptyState
            icon="📂"
            title="هنوز دسته‌بندی‌ای تعریف نشده"
            description="با افزودن اولین دسته‌بندی، آن را در اختیار معلمان قرار دهید."
            action={
              <button
                className="ab-btn ab-btn-primary"
                onClick={() => setAddingCategory(true)}
              >
                ➕ افزودن اولین دسته‌بندی
              </button>
            }
          />
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          {categories.map((cat, idx) => {
            const count = projectCountByCategory.get(cat.id) || 0;
            return (
              <div
                key={cat.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  background: '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 12,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: '#eef2ff', color: '#4f46e5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '0.85rem',
                  flexShrink: 0,
                }}>
                  {idx + 1}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 800,
                    fontSize: '1rem',
                    color: '#1e293b',
                  }}>
                    {cat.name}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                    {count > 0 ? `${count} پروژه از این دسته استفاده می‌کند` : 'بدون پروژه'}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="ab-btn ab-btn-ghost"
                    onClick={() => handleMove(cat, 'up')}
                    disabled={idx === 0}
                    title="بالا"
                    style={{ padding: '6px 10px' }}
                  >↑</button>
                  <button
                    className="ab-btn ab-btn-ghost"
                    onClick={() => handleMove(cat, 'down')}
                    disabled={idx === categories.length - 1}
                    title="پایین"
                    style={{ padding: '6px 10px' }}
                  >↓</button>
                  <button
                    className="ab-btn ab-btn-ghost"
                    onClick={() => handleStartRename(cat)}
                    title="ویرایش نام"
                    style={{ padding: '6px 10px' }}
                  >✏️</button>
                  <button
                    className="ab-btn ab-btn-ghost"
                    onClick={() => handleDelete(cat)}
                    title="حذف"
                    style={{ padding: '6px 10px', color: '#ef4444' }}
                  >🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* افزودن */}
      <Modal
        isOpen={addingCategory}
        onClose={() => { setAddingCategory(false); setNewCategoryName(''); }}
        title="افزودن دسته‌بندی جدید"
        size="sm"
        footer={
          <>
            <button
              className="ab-btn ab-btn-ghost"
              onClick={() => { setAddingCategory(false); setNewCategoryName(''); }}
            >انصراف</button>
            <button
              className="ab-btn ab-btn-primary"
              onClick={handleAdd}
              disabled={!newCategoryName.trim()}
            >افزودن</button>
          </>
        }
      >
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 700, fontSize: '0.85rem' }}>
            نام دسته‌بندی
          </label>
          <input
            autoFocus
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="مثلاً: زبان‌های خارجی"
            maxLength={100}
            style={{
              width: '100%', padding: '10px 14px',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontFamily: 'inherit', fontSize: '0.9rem',
            }}
          />
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 6 }}>
            {newCategoryName.length} / 100
          </div>
        </div>
      </Modal>

      {/* ویرایش */}
      <Modal
        isOpen={!!editingCategory}
        onClose={() => setEditingCategory(null)}
        title="ویرایش نام دسته‌بندی"
        size="sm"
        footer={
          <>
            <button
              className="ab-btn ab-btn-ghost"
              onClick={() => setEditingCategory(null)}
            >انصراف</button>
            <button
              className="ab-btn ab-btn-primary"
              onClick={handleRename}
              disabled={!editingName.trim() || editingName.trim() === editingCategory?.name}
            >ذخیره</button>
          </>
        }
      >
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 700, fontSize: '0.85rem' }}>
            نام جدید
          </label>
          <input
            autoFocus
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
            maxLength={100}
            style={{
              width: '100%', padding: '10px 14px',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontFamily: 'inherit', fontSize: '0.9rem',
            }}
          />
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#eff6ff', borderRadius: 10,
            fontSize: '0.8rem', color: '#1e40af', lineHeight: 1.7,
          }}>
            ℹ️ با تغییر نام، این دسته‌بندی در همه‌ی پروژه‌هایی که از آن استفاده می‌کنند به‌روزرسانی می‌شود.
          </div>
        </div>
      </Modal>

      {/* حذف */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteCategory}
        title="حذف دسته‌بندی"
        message={
          confirmDelete?.affectedCount > 0
            ? `این دسته‌بندی در ${confirmDelete.affectedCount} پروژه استفاده می‌شود. با حذف، این پروژه‌ها بدون دسته‌بندی خواهند شد (خود پروژه‌ها حذف نمی‌شوند).`
            : `آیا از حذف دسته‌بندی «${confirmDelete?.category?.name}» مطمئن هستید؟`
        }
        confirmText="🗑️ حذف"
        cancelText="انصراف"
        tone="danger"
      />
    </div>
  );
}